import React, { FC } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { UploadProgressBar } from '../../components/upload-progress';
import { PLAYLIST_ID_PARAM_NAME } from '../../hooks/use-playlist-path-id';
import { RESET_TOKEN_PARAM } from '../../hooks/use-reset-token-path';
import {
  VIDEO_ID_PARAM_NAME,
  VIDEO_LINK_PARAM_NAME,
} from '../../hooks/use-video-path-id';
import { AuthLayout } from '../../layouts/auth-layout';
import { BaseLayout } from '../../layouts/base-layout';
import { Account } from '../../pages/account';
import { AnalyticsPage } from '../../pages/analytics';
import { FinancePage } from '../../pages/finance';
import { ForgotPassword } from '../../pages/forgot-password';
import { ForgotPasswordComplete } from '../../pages/forgot-password-complete';
import { LinkStatistic } from '../../pages/link-statistic';
import { Login } from '../../pages/login';
import { PlayCourseDetailPage } from '../../pages/play-course';
import { PlayCourseItemPage } from '../../pages/play-course-item';
import { PlayCoursesPage } from '../../pages/play-courses';
import { PlaylistPage } from '../../pages/playlist';
import { PrivacyPolicyPage } from '../../pages/privacy-policy';
import { PublicOffer } from '../../pages/public-offer';
import { Registration } from '../../pages/registration';
import { UploadedVideos } from '../../pages/uploaded-videos';
import { Video } from '../../pages/video';
import { VideoCreate } from '../../pages/video-create';
import { VideoStatistics } from '../../pages/video-statistics';
import { ViewedVideos } from '../../pages/viewed-videos';
import { AiManagerModule1Page } from '../../pages/aimanager-module1';
import { Module1Page } from '../../pages/module1';
import { Module1ViewerPage } from '../../pages/module1-viewer';
import { UsersAccessPage } from '../../pages/users-access';
import { StudentLogin } from '../../pages/student-login';
import { StudentPlaylistItemPage } from '../../pages/student-playlist-item';
import { StudentPlaylistsPage } from '../../pages/student-playlists';
import { Paths, SecondaryPaths } from '../../types/common';

export const RootRoutes: FC = () => {
  return (
    <Routes>
      <Route element={<AuthLayout />}>
        <Route path={Paths.LOGIN} element={<Login />} />
        <Route path={Paths.LOGIN_STUDENT} element={<StudentLogin />} />
        <Route path={Paths.REGISTRATION} element={<Registration />} />
        <Route path={Paths.FORGOT_PASSWORD} element={<ForgotPassword />} />
        <Route
          path={`${Paths.FORGOT_COMPLETE}/:${RESET_TOKEN_PARAM}`}
          element={<ForgotPasswordComplete />}
        />
      </Route>

      <Route path={Paths.PRIVACY_POLICY} element={<PrivacyPolicyPage />} />
      <Route path={Paths.PUBLIC_OFFER} element={<PublicOffer />} />

      <Route element={<BaseLayout />}>
        <Route path={Paths.BASE} element={<Navigate to={Paths.VIDEO} replace />} />
        <Route path={Paths.MODULE1} element={<Module1Page />} />
        <Route path={Paths.MODULE1_VIEW} element={<Module1ViewerPage />} />
        <Route path={Paths.AIMANAGER_MODULE1} element={<AiManagerModule1Page />} />
        <Route path={Paths.AIMANAGER_MODULE1_VIEW} element={<Module1ViewerPage />} />
        <Route path={Paths.USERS_ACCESS} element={<UsersAccessPage />} />
        <Route path={Paths.VIDEO} element={<UploadedVideos />} />
        <Route
          path={Paths.FILES}
          element={<Module1Page assetFilter="files" />}
        />
        <Route
          path={Paths.UPLOADED_VIDEO}
          element={<Module1Page assetFilter="video" />}
        />
        <Route
          path={Paths.UPLOADED_FILES}
          element={<Module1Page assetFilter="files" />}
        />
        <Route
          path={`${Paths.UPLOADED_VIDEOS}/*`}
          element={<UploadedVideos />}
        />
        <Route path={`${Paths.VIEWED_VIDEOS}/*`} element={<ViewedVideos />} />
        <Route path={Paths.ANALYTICS} element={<AnalyticsPage />} />
        <Route path={Paths.FINANCE} element={<FinancePage />} />
        <Route path={Paths.ACCOUNT} element={<Account />} />
        <Route path={Paths.PLAYLISTS} element={<StudentPlaylistsPage />} />
        <Route
          path={`${Paths.PLAYLISTS}/:${PLAYLIST_ID_PARAM_NAME}`}
          element={<PlaylistPage />}
        />
        <Route
          path={`${Paths.PLAYLISTS}/:${PLAYLIST_ID_PARAM_NAME}/items/:itemIndex`}
          element={<StudentPlaylistItemPage />}
        />
        <Route path={Paths.PLAYCOURSE} element={<PlayCoursesPage />} />
        <Route
          path={`${Paths.PLAYCOURSE}/:${PLAYLIST_ID_PARAM_NAME}`}
          element={<PlayCourseDetailPage />}
        />
        <Route
          path={`${Paths.PLAYCOURSE}/:${PLAYLIST_ID_PARAM_NAME}/modules`}
          element={<PlayCourseDetailPage />}
        />
        <Route
          path={`${Paths.PLAYCOURSE}/:${PLAYLIST_ID_PARAM_NAME}/modules/:moduleKey`}
          element={<PlayCourseDetailPage />}
        />
        <Route
          path={`${Paths.PLAYCOURSE}/:${PLAYLIST_ID_PARAM_NAME}/modules/:moduleKey/topics/:topicKey`}
          element={<PlayCourseDetailPage />}
        />
        <Route
          path={`${Paths.PLAYCOURSE}/:${PLAYLIST_ID_PARAM_NAME}/modules/:moduleKey/topics/:topicKey/lessons/:lessonKey`}
          element={<PlayCourseDetailPage />}
        />
        <Route
          path={`${Paths.PLAYCOURSE}/:${PLAYLIST_ID_PARAM_NAME}/modules/:moduleKey/topics/:topicKey/lessons/:lessonKey/files/:itemIndex`}
          element={<PlayCourseItemPage />}
        />
        <Route
          path={`${Paths.PLAYCOURSE}/:${PLAYLIST_ID_PARAM_NAME}/modules/:moduleKey/topics/:topicKey/lessons/:lessonKey/subtopics/:subtopicKey`}
          element={<PlayCourseDetailPage />}
        />
        <Route
          path={`${Paths.PLAYCOURSE}/:${PLAYLIST_ID_PARAM_NAME}/modules/:moduleKey/topics/:topicKey/lessons/:lessonKey/subtopics/:subtopicKey/files/:itemIndex`}
          element={<PlayCourseItemPage />}
        />
        <Route
          path={`${Paths.PLAYCOURSE}/:${PLAYLIST_ID_PARAM_NAME}/items/:itemIndex`}
          element={<PlayCourseItemPage />}
        />
        <Route path={Paths.VIDEO_CREATE} element={<VideoCreate />} />
        <Route
          path={`${Paths.VIDEO}/:${VIDEO_ID_PARAM_NAME}${SecondaryPaths.STATISTICS}`}
          element={<VideoStatistics />}
        />
        <Route
          path={`${Paths.VIDEO}/:${VIDEO_ID_PARAM_NAME}/*`}
          element={<Video />}
        />
        <Route
          path={`${Paths.VIDEO}/:${VIDEO_ID_PARAM_NAME}${SecondaryPaths.STATISTICS}/:${VIDEO_LINK_PARAM_NAME}`}
          element={<LinkStatistic />}
        />
        <Route
          path={`${Paths.PLAYLIST}/:${PLAYLIST_ID_PARAM_NAME}`}
          element={<PlaylistPage />}
        />
        <Route
          path={`${Paths.PLAYCOURSE_ADMIN}/:${PLAYLIST_ID_PARAM_NAME}`}
          element={<PlaylistPage />}
        />
        <Route
          path={`${Paths.PLAYLIST}/:${PLAYLIST_ID_PARAM_NAME}${SecondaryPaths.PLAYLIST_ADD_VIDEO}`}
          element={<>some page</>}
        />
      </Route>
    </Routes>
  );
};
